import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import ControlledInput from '../common/forms/ControlledInput';
import AlxButton from '../alx-components/button';
import { RootState } from '../../store/rootReducer';
import useAssistance from '../../hooks/useAssistance/useAssistance';
import { ContentLayout, FormLayout } from '../../global-styles/layout';
import { getErrorMessage } from '../../constants/errorMessages';
import { requestACallByPhone } from '../../api/assistance/assistance';
import { WantToCallMeStyles } from './want-to-call-me';

interface WantToCallMeProps {
  intervention?: string;
}

const WantToCallMe: React.FC<WantToCallMeProps> = ({ intervention }) => {
  const { t } = useTranslation(['wantToCallMe']);
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<{ phone: string }>({
    defaultValues: {
      phone: '',
    },
  });

  const { requestFilePhone } = useAssistance({ intervention });
  const { data } = requestFilePhone;
  const providerCode = useSelector((state: RootState) => state.general.provider);

  const requestACallMutation = useMutation({
    mutationFn: (phone: string) => requestACallByPhone(phone, intervention!, providerCode),
  });

  return (
    <ContentLayout data-cy="want-to-callme-page">
      <WantToCallMeStyles>
        <FormLayout>
          <div className="invalid-text">{t('wantToCallMe:text1')}</div>
          <AlxButton
            onClick={() => {
              requestACallMutation.mutate(data?.telefono || '', {
                onSuccess: () => navigate('/callme-back'),
              });
            }}
            data-cy="file-call-button"
          >
            {data?.telefono || ''}
          </AlxButton>{' '}
          <div className="customNumber">{t('wantToCallMe:text2')}</div>
          <ControlledInput
            inputProps={{
              variant: 'filled',
              label: t('wantToCallMe:text3'),
              'data-cy': 'phone',
              error: !!errors?.phone,
              helperText: getErrorMessage(t, errors.phone?.type),
              filter: 'phone',
            }}
            controllerProps={{
              control,
              name: 'phone',
              rules: {
                required: true,
              },
            }}
          />
          <AlxButton
            onClick={handleSubmit(({ phone }) => {
              if (!phone) return;
              requestACallMutation.mutate(phone, {
                onSuccess: () => navigate('/callme-back'),
              });
            })}
            data-cy="own-phone-button"
          >
            {t('wantToCallMe:text4')}
          </AlxButton>
        </FormLayout>
      </WantToCallMeStyles>
    </ContentLayout>
  );
};

export default WantToCallMe;
